import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { ArrowUpRight, ArrowDownRight, Plus, Send, Repeat, Clock } from "lucide-react";
import { api, formatFiat, formatCrypto, formatPct, FIATS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { DASHBOARD } from "@/constants/testIds";
import Sparkline from "@/components/Sparkline";

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const { fiat } = useOutletContext();
  const [wallet, setWallet] = useState(null);
  const [markets, setMarkets] = useState([]);
  const [fxRates, setFxRates] = useState({ USD: 1, CAD: 0.73, EUR: 1.08 });
  const [pendingWithdrawals, setPendingWithdrawals] = useState([]);

  const load = useCallback(async () => {
    const [w, m, fx, txs] = await Promise.all([
      api.get("/wallet"),
      api.get("/markets"),
      api.get("/fx-rates"),
      api.get("/transactions"),
    ]);
    setWallet(w.data);
    setMarkets(m.data.assets);
    setFxRates(fx.data.rates);
    setPendingWithdrawals(
      (txs.data.transactions || []).filter(
        (x) => x.type === "withdraw" && x.status === "pending"
      )
    );
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(load, 60000);
    return () => clearInterval(i);
  }, [load]);

  const fxRate = fxRates[fiat] || 1;
  const totalFiat = useMemo(() => (wallet ? wallet.total_usd / fxRate : 0), [wallet, fxRate]);
  const visibleBalances = useMemo(
    () => (wallet?.balances || []).filter((b) => b.balance > 0),
    [wallet]
  );

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs tracking-[0.3em] uppercase text-[#6E7587]">{t("dashboard.welcome")}</div>
        <h1 className="font-display text-3xl font-bold mt-1">{user?.full_name}</h1>
      </div>

      {pendingWithdrawals.length > 0 && (
        <Link
          to="/app/transactions"
          data-testid="dashboard-pending-banner"
          className="block border border-[#D19B54]/40 bg-[#D19B54]/5 hover:bg-[#D19B54]/10 transition-colors p-4 rounded-sm"
        >
          <div className="flex items-center gap-3">
            <Clock size={18} className="text-[#D19B54] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#D19B54]">
                {t("dashboard.pending_eyebrow")}
              </div>
              <div className="text-sm mt-0.5">
                {t("dashboard.pending_text", { count: pendingWithdrawals.length })}
              </div>
            </div>
            <ArrowUpRight size={16} className="text-[#D19B54]" />
          </div>
        </Link>
      )}

      {/* Hero balance card */}
      <div className="border border-[#2A2E38] bg-gradient-to-br from-[#141720] to-[#1C202B] p-8 grain">
        <div className="flex items-start justify-between flex-wrap gap-6">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A9B8]">{t("dashboard.total_portfolio")} · {fiat}</div>
            <div
              data-testid={DASHBOARD.totalBalance}
              className="font-mono text-5xl md:text-6xl font-medium mt-3 tracking-tight"
            >
              {formatFiat(totalFiat, fiat)}
            </div>
            <div className="text-xs text-[#6E7587] font-mono mt-3">
              ≈ {formatFiat(wallet?.total_usd || 0, "USD")}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/app/wallet"
              data-testid={DASHBOARD.depositButton}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#7C5CFF] text-white rounded-sm hover:bg-[#9C82FF] transition-colors text-sm"
            >
              <Plus size={14} /> {t("dashboard.deposit")}
            </Link>
            <Link
              to="/app/wallet"
              data-testid={DASHBOARD.withdrawButton}
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#2A2E38] rounded-sm hover:border-[#7C5CFF] hover:text-[#7C5CFF] transition-colors text-sm"
            >
              <Send size={14} /> {t("dashboard.withdraw")}
            </Link>
            <Link
              to="/app/trade"
              data-testid={DASHBOARD.tradeButton}
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#2A2E38] rounded-sm hover:border-[#7C5CFF] hover:text-[#7C5CFF] transition-colors text-sm"
            >
              <Repeat size={14} /> {t("dashboard.trade")}
            </Link>
          </div>
        </div>
      </div>

      {/* Balances grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-[#2A2E38] bg-[#141720]">
          <div className="px-6 py-4 border-b border-[#2A2E38] flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587]">{t("dashboard.balances")}</div>
              <h3 className="font-display text-lg font-bold mt-1">{t("dashboard.your_assets")}</h3>
            </div>
            <Link to="/app/wallet" className="text-xs text-[#7C5CFF] hover:text-[#9C82FF]">{t("dashboard.manage")} →</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] tracking-[0.2em] uppercase text-[#6E7587]">
                <tr className="border-b border-[#2A2E38]">
                  <th className="text-left px-6 py-3 font-normal">{t("dashboard.asset")}</th>
                  <th className="text-right px-6 py-3 font-normal">{t("dashboard.balance")}</th>
                  <th className="text-right px-6 py-3 font-normal">{fiat} {t("dashboard.value")}</th>
                </tr>
              </thead>
              <tbody>
                {visibleBalances.map((b) => (
                    <tr key={b.currency} className="border-b border-[#2A2E38] hover:bg-[#1C202B]">
                      <td className="px-6 py-4">
                        <div className="font-medium">{b.name}</div>
                        <div className="text-xs text-[#6E7587] font-mono uppercase">
                          {b.kind} · {b.currency}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right font-mono">
                        {b.kind === "fiat" ? formatFiat(b.balance, b.currency) : formatCrypto(b.balance)}
                      </td>
                      <td className="px-6 py-4 text-right font-mono">
                        {formatFiat(b.usd_value / fxRate, fiat)}
                      </td>
                    </tr>
                  ))}
                {(!wallet || visibleBalances.length === 0) && (
                  <tr><td colSpan={3} className="px-6 py-8 text-center text-[#6E7587]">{t("dashboard.no_balances")}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="border border-[#2A2E38] bg-[#141720]">
          <div className="px-6 py-4 border-b border-[#2A2E38]">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587]">{t("dashboard.movers")}</div>
            <h3 className="font-display text-lg font-bold mt-1">{t("dashboard.top_24h")}</h3>
          </div>
          <div className="divide-y divide-[#2A2E38]">
            {markets.slice(0, 6).map((a) => (
              <Link
                key={a.symbol}
                to={`/app/markets/${a.symbol}`}
                className="px-6 py-3 flex items-center gap-3 hover:bg-[#1C202B] transition-colors"
              >
                {a.image && <img src={a.image} alt="" className="w-6 h-6" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{a.symbol}</div>
                  <div className="text-xs text-[#6E7587] font-mono">${Number(a.price_usd).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                </div>
                <Sparkline data={a.sparkline} width={70} height={24} color={a.change_24h >= 0 ? "#7C9C69" : "#C75C5C"} />
                <div className={`text-xs font-mono ${a.change_24h >= 0 ? "text-[#7C9C69]" : "text-[#C75C5C]"}`}>
                  {formatPct(a.change_24h)}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
